import { watch } from 'vue'

import { useLocationStore } from '@common/location-layer/store/location.store'

import { useGroupsUIStore } from '../stores/groups.store'

const abortError = () => new DOMException('Group location resolution aborted', 'AbortError')

/**
 * Resolves locationId once the loaded group matches the route slug.
 * Waits on the same groups UI store that `useGroupInitialization` populates.
 */
export function resolveGroupLocation(routeSlug: string, signal: AbortSignal): Promise<string> {
  const groupsStore = useGroupsUIStore()
  const locationStore = useLocationStore()

  return new Promise<string>((resolve, reject) => {
    let settled = false
    let stopWatching: (() => void) | undefined

    const cleanup = () => {
      signal.removeEventListener('abort', onAbort)
      stopWatching?.()
    }

    const finish = (locationId: string) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(locationId)
    }

    const fail = (error: unknown) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    const onAbort = () => fail(abortError())

    const tryResolve = (): boolean => {
      const group = groupsStore.currentGroup
      if (groupsStore.isCurrentGroupLoading) return false
      if (!group?.slug || group.slug !== routeSlug) return false

      const locationId = group.locationId?.trim() || locationStore.locationId?.trim()
      if (!locationId) return false

      finish(locationId)
      return true
    }

    try {
      if (signal.aborted) {
        onAbort()
        return
      }

      signal.addEventListener('abort', onAbort, { once: true })
      if (tryResolve()) return

      stopWatching = watch(
        () => [groupsStore.currentGroup, groupsStore.isCurrentGroupLoading, locationStore.locationId] as const,
        () => {
          try {
            tryResolve()
          } catch (error) {
            fail(error)
          }
        }
      )

      tryResolve()
    } catch (error) {
      fail(error)
    }
  })
}
